import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bulletinApi, BulletinListParams } from '@/api/bulletin';
import { Bulletin } from '@/types';
import { useActionFeedback } from '@/hooks/useActionFeedback';

export const useBulletinList = (params: BulletinListParams = {}) => {
  return useQuery({
    queryKey: ['bulletin', params],
    queryFn: () => bulletinApi.getAll(params),
  });
};

export const useBulletin = (id: number) => {
  return useQuery({
    queryKey: ['bulletin', id],
    queryFn: () => bulletinApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateBulletin = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (data: Partial<Bulletin>) => bulletinApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['bulletin'] });
      feedback.success('Postingan berhasil dibuat');
      return response.message;
    },
    onError: (error) => {
      feedback.error('Gagal membuat postingan', error);
    },
  });
};

export const useUpdateBulletin = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Bulletin> }) =>
      bulletinApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulletin'] });
      feedback.success('Postingan berhasil diperbarui');
    },
    onError: (error) => {
      feedback.error('Gagal memperbarui postingan', error);
    },
  });
};

export const useDeleteBulletin = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (id: number) => bulletinApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulletin'] });
      feedback.success('Postingan berhasil dihapus');
    },
    onError: (error) => {
      feedback.error('Gagal menghapus postingan', error);
    },
  });
};

export const useUploadBulletinCover = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => bulletinApi.uploadCover(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulletin'] });
      feedback.success('Cover postingan berhasil diunggah');
    },
    onError: (error) => {
      feedback.error('Gagal mengunggah cover postingan', error);
    },
  });
};

export const useSetBulletinCoverLink = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: ({ id, url }: { id: number; url: string }) => bulletinApi.setCoverLink(id, url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulletin'] });
      feedback.success('Cover postingan berhasil diperbarui');
    },
    onError: (error) => {
      feedback.error('Gagal memperbarui cover postingan', error);
    },
  });
};
